/* eslint-disable no-nested-ternary */
import { useHistory, useLocation, useParams } from "react-router";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useManageFeatureInstanceDetails } from "entity-app/hooks/feature-details-page";
import {
  isPermissionNotGrantted,
  ProjectContext
} from "context/ProjectProvider";
import {
  CompanyParticipationType,
  ESubmittalStatus,
  ProjectPermissionEnum,
  UserParticipationType
} from "constants/index";
import { AttachmentDoc } from "models/submittal-log";
import { useCIQMutation, useCIQQuery } from "hooks/ciq-gql-hooks";
import { FEATURE_ATTACHMENTS } from "services/graphQL/ciq-queries";
import { getSubmittalStatus, isProjectInIntegrationMode } from "utils/utils";
import { message } from "antd";
import { MUTATION_UPDATE_ATTACHMENT_BY_PK } from "services/graphQL/mutations";
import { generateS3URL } from "services/file-mgmt";
import { noAuthClient } from "services/axios";
import { getEditableFields } from "components/submittal-details/helpers";
import { FeatureTypes } from "entity-app/constants";
import FeatureHeader from "./submittal-detail-header";
import SubmittalDetailsCards from "./submittal-details-cards";
import SubmittalAttachments from "../submittal-attachment";

function NewSubmittalDetailsPage() {
  const history = useHistory();
  const { projectId, submittalId } = useParams() as any;
  const {
    tokenContents,
    gqlClientForProject,
    projectDetails,
    tokenRetrievalState,
    eventLogs,
    columnHeaders: { submittalHeaderMap },
    projectIntegrationConfig
  } = useContext(ProjectContext);
  const location = useLocation();
  const tab = new URLSearchParams(location.search).get("tab");
  const [tabIndex, setTabIndex] = useState<string>(tab || "1");
  const paramAttachmentId = new URLSearchParams(location.search).get("aId");
  const previousEventLogs = useRef(eventLogs);

  const { featureInstanceData, actions } = useManageFeatureInstanceDetails(
    submittalId,
    FeatureTypes.SUBMITTALS
  );

  const [isRevisionMode, setRevisionMode] = useState(false);
  const [isWorkflowInOverrideMode, setIsWorkflowInOverrideMode] =
    useState(false);
  const [localFeatureInstanceData, setLocalFeatureInstanceData] =
    useState<any>();

  useEffect(() => {
    if (!featureInstanceData) return;
    setLocalFeatureInstanceData(structuredClone(featureInstanceData));
  }, [featureInstanceData]);

  const submittal = useMemo(() => {
    if (!localFeatureInstanceData?.feature_submittals?.length) return null;
    return localFeatureInstanceData?.feature_submittals[0];
  }, [localFeatureInstanceData?.feature_submittals]);

  const [submittalViewState, setSubmittalViewState] = useState<{
    status: number;
    userType: string;
  }>({
    status: submittal?.status,
    userType: tokenContents?.userType as string
  });

  const cannotRespondToWorkflow: boolean = useMemo(() => {
    return isPermissionNotGrantted(
      ProjectPermissionEnum.SubmittalWorkflow,
      tokenContents?.role!
    );
  }, [tokenContents]);

  const isIntegrationMode = isProjectInIntegrationMode(
    projectDetails ? projectDetails.mode : 0
  );

  const [addMore, setAddMore] = useState(false);

  const [deleteAttachmentMutationPK] = useCIQMutation(
    MUTATION_UPDATE_ATTACHMENT_BY_PK,
    { client: gqlClientForProject }
  );

  const [attachmentDocuments, setAttachmentDocuments] = useState<{
    documents: Array<AttachmentDoc>;
    referenceDocs: Array<AttachmentDoc>;
  }>({ documents: [], referenceDocs: [] });

  const [selectedDocument, setSelectedDocument] = useState<
    AttachmentDoc | undefined
  >();

  const { data: dataAllAttachments, refetch: refetchAttachments } = useCIQQuery(
    FEATURE_ATTACHMENTS,
    {
      variables: {
        where: {
          feature_instance_id: { _eq: localFeatureInstanceData?.id },
          version_of: { _is_null: true }
        }
      },
      client: gqlClientForProject,
      skip: !gqlClientForProject || !localFeatureInstanceData?.id
    }
  );

  useEffect(() => {
    if (dataAllAttachments) {
      const { attachments } = dataAllAttachments;
      setAttachmentDocuments({
        documents: attachments.filter((x: AttachmentDoc) => !x.reference_id),
        referenceDocs: attachments.filter((y: AttachmentDoc) => y.reference_id)
      });
    }
  }, [dataAllAttachments]);

  const goBackToList = () => {
    history.push(`/project/${projectId}/submittals`);
  };

  useEffect(() => {
    if (eventLogs?.length && previousEventLogs.current !== eventLogs) {
      if (
        eventLogs.some(
          (e) =>
            e?.data_source === "feature_instance" &&
            e?.info?.feature_instance_ids?.includes(submittalId)
        )
      ) {
        refetchAttachments();
      }
      if (
        eventLogs.some(
          (e) =>
            e?.data_source === "attachments" &&
            attachmentDocuments?.documents.some((x) => x.id === e.object_id)
        )
      ) {
        refetchAttachments();
      }
    }
    previousEventLogs.current = eventLogs;
  }, [
    actions,
    attachmentDocuments?.documents,
    eventLogs,
    refetchAttachments,
    submittalId
  ]);
  const submittalParticipants = useMemo(() => {
    const contractor = featureInstanceData?.feature_company_participants?.find(
      (company: any) =>
        company?.company_participation_type_id ===
        CompanyParticipationType.RESPONSIBLE_CONTRACTOR
    );

    const drCompany = featureInstanceData?.feature_company_participants?.find(
      (company: any) =>
        company?.company_participation_type_id ===
        CompanyParticipationType.DESIGN_REVIEWER_VENDOR
    );

    const assignee = featureInstanceData?.feature_user_paticipants?.find(
      (user: any) =>
        user?.user_participation_type?.id === UserParticipationType.ASSIGNEE
    );

    const assigneeCompany: any = assignee?.user?.project_users?.length
      ? assignee?.user?.project_users[0].subscription_vendor?.name ?? ""
      : "";

    const submitter = featureInstanceData?.feature_user_paticipants?.find(
      (user: any) =>
        user?.user_participation_type?.id === UserParticipationType.SUBMITTER
    );

    const gcReviewer = featureInstanceData?.feature_user_paticipants?.find(
      (user: any) =>
        user?.user_participation_type?.id === UserParticipationType.GC_REVIEWER
    );

    const drReviewer = featureInstanceData?.feature_user_paticipants?.find(
      (user: any) => {
        return (
          user?.user_participation_type?.id ===
          UserParticipationType.DESIGN_REVIEWER
        );
      }
    );

    const assigneeUnregistered = () => {
      if (assignee?.user_unregistered) {
        if (assignee?.user_unregistered === drReviewer?.user_unregistered) {
          return {
            id: `${drReviewer?.user_unregistered}`,
            fullName: `${drReviewer?.user_unregistered}`,
            company: {
              name:
                drCompany?.vendor_unregistered ?? drCompany?.vendor?.name ?? ""
            },
            type: "poc"
          };
        }
        return {
          id: `${submitter?.user_unregistered}`,
          fullName: `${submitter?.user_unregistered}`,
          company: {
            name:
              contractor?.vendor_unregistered ?? contractor?.vendor?.name ?? ""
          },
          type: "poc"
        };
      }
      return null;
    };

    return {
      contractor,
      assignee: assignee?.user
        ? assignee
        : assignee?.user_source_info
        ? {
            user: {
              id: `${assignee?.user_source_info?.first_name} ${assignee?.user_source_info?.last_name}`,
              first_name: `${assignee?.user_source_info?.first_name} ${assignee?.user_source_info?.last_name}`,
              vendor_source_info: assignee?.user_source_info?.vendor_source_info
            }
          }
        : null,
      submitter: submitter?.user
        ? submitter
        : submitter?.user_unregistered
        ? {
            user: {
              id: submitter.user_unregistered,
              first_name: submitter.user_unregistered
            }
          }
        : submitter?.user_source_info
        ? {
            user: {
              id: `${submitter?.user_source_info?.first_name} ${submitter?.user_source_info?.last_name}`,
              first_name: `${submitter?.user_source_info?.first_name} ${submitter?.user_source_info?.last_name}`,
              vendor_source_info:
                submitter?.user_source_info?.vendor_source_info
            }
          }
        : null,
      gcReviewer: gcReviewer?.user
        ? gcReviewer
        : gcReviewer?.user_source_info
        ? {
            user: {
              id: `${gcReviewer?.user_source_info?.first_name} ${gcReviewer?.user_source_info?.last_name}`,
              first_name: `${gcReviewer?.user_source_info?.first_name} ${gcReviewer?.user_source_info?.last_name}`,
              vendor_source_info:
                gcReviewer?.user_source_info?.vendor_source_info
            }
          }
        : null,
      drReviewer: drReviewer?.user
        ? drReviewer
        : drReviewer?.user_unregistered
        ? {
            user: {
              id: drReviewer.user_unregistered,
              first_name: drReviewer.user_unregistered
            }
          }
        : drReviewer?.user_source_info
        ? {
            user: {
              id: `${drReviewer?.user_source_info?.first_name} ${drReviewer?.user_source_info?.last_name}`,
              first_name: `${drReviewer?.user_source_info?.first_name} ${drReviewer?.user_source_info?.last_name}`,
              vendor_source_info:
                drReviewer?.user_source_info?.vendor_source_info
            }
          }
        : null,
      drCompany,
      assigneeCompany,
      assigneeUnregistered
    };
  }, [
    featureInstanceData?.feature_company_participants,
    featureInstanceData?.feature_user_paticipants
  ]);

  const editableFields: string[] = useMemo(() => {
    if (!submittal) return [];
    const tmpEditableFields = getEditableFields(
      submittalViewState,
      localFeatureInstanceData,
      tokenContents,
      submittal,
      submittalParticipants
    );
    if (isWorkflowInOverrideMode) {
      tmpEditableFields.push("attachment");
    }
    return tmpEditableFields;
  }, [
    submittalViewState,
    localFeatureInstanceData,
    tokenContents,
    isWorkflowInOverrideMode,
    submittal,
    submittalParticipants
  ]);

  useEffect(() => {
    if (
      gqlClientForProject &&
      tokenRetrievalState?.success &&
      featureInstanceData &&
      tokenContents &&
      submittal
    ) {
      if (!isRevisionMode && tokenContents) {
        const submittalStatus = getSubmittalStatus(
          submittal?.submittal_status_history_details
        );

        setSubmittalViewState({
          userType: tokenContents.userType,
          status: submittalStatus || ESubmittalStatus.CREATE
        });
      }
    }
  }, [
    submittal,
    gqlClientForProject,
    tokenRetrievalState,
    featureInstanceData,
    tokenContents,
    isRevisionMode
  ]);

  const RemoveAttachment = async (attachmentId: string) => {
    try {
      const updateResponse = await deleteAttachmentMutationPK({
        variables: {
          id: attachmentId,
          set: { deleted: true }
        }
      });

      if (updateResponse && updateResponse.data) {
        message.success("Removed successfully");
        if (paramAttachmentId) {
          history.replace(
            `/project/${projectId}/submittals/${submittalId}?tab=2`
          );
        }
        if (selectedDocument && selectedDocument?.id === attachmentId) {
          setSelectedDocument(undefined);
        }
      }
      if (updateResponse && updateResponse.errors) {
        message.error(updateResponse.errors[0].message);
      }
    } catch (ex: any) {
      message.error(ex.message);
    }
  };

  const getLatestWFHistoryItem = (historyItems: any) => {
    if (!historyItems) return null;
    const historyArr = historyItems.filter(
      (historyItem: any) =>
        !(
          historyItem.action &&
          historyItem.action.toLowerCase().includes("deleted")
        )
    );
    if (historyArr && historyArr?.length > 0) {
      return historyArr[0];
    }
    return null;
  };

  const getOnBehalfOfInfoObj = () => {
    let onBehalfOfInfoObj: any = null;
    if (isWorkflowInOverrideMode) {
      onBehalfOfInfoObj = {};
      if (submittalParticipants?.assignee?.user?.id) {
        onBehalfOfInfoObj.on_behalf_of_user_id =
          submittalParticipants?.assignee?.user?.id;
      } else if (submittalParticipants?.assigneeUnregistered()) {
        onBehalfOfInfoObj.on_behalf_of_user_id =
          submittalParticipants?.assigneeUnregistered()?.id;
      }
    }
    return onBehalfOfInfoObj;
  };

  const UploadAttachment = async ({ file, onSuccess }: any) => {
    let requestPayload = {
      feature: "SUBMITTALS",
      feature_id: submittalId,
      file_name: file.name
    };
    const onBehalfOfInfo = getOnBehalfOfInfoObj();
    if (onBehalfOfInfo) {
      requestPayload = { ...requestPayload, ...onBehalfOfInfo };
    }

    const historyItem = getLatestWFHistoryItem(
      submittal?.submittal_status_history_details || []
    );

    const subHistoryObj: any = {};
    if (historyItem) {
      subHistoryObj.submittal_status_history_id = `${historyItem.id}`;
      requestPayload = {
        ...requestPayload,
        ...subHistoryObj
      };
    }
    const signedUrlRespose: any = await generateS3URL(
      requestPayload,
      tokenRetrievalState.token
    );

    if (signedUrlRespose.error) {
      message.error(signedUrlRespose.error.msg);
      return null;
    }

    const fileUploadResponse: any = await noAuthClient.put(
      `${signedUrlRespose.data.success.url}`,
      file,
      {
        onUploadProgress: (progressEvent) => {
          const progressCount = Math.ceil(
            (progressEvent.loaded / progressEvent.total) * 100
          );
          console.log(progressCount);
          // if (setProgress) setProgress(progressCount);
        }
      }
    );
    if (fileUploadResponse.error) {
      message.error(signedUrlRespose.error.msg);
      return null;
      // reject(new Error(fileUploadResponse.error));
    }
    onSuccess("OK");
    return fileUploadResponse;
  };

  const findLatestAttachmentVersion = useCallback(
    (doc: AttachmentDoc) => {
      const latestVersion =
        doc.versions.length === 0 ? doc : doc.versions.slice(-1)[0];
      const lastRef = attachmentDocuments?.referenceDocs.find(
        (d) => d.reference_id === latestVersion.id
      );
      return lastRef || latestVersion;
    },
    [attachmentDocuments?.referenceDocs]
  );

  const submittalCardContainer = () => {
    switch (tabIndex) {
      case "1":
        return (
          <SubmittalDetailsCards
            featureInstanceData={featureInstanceData}
            actions={actions}
            featureId={submittalId}
            setTabIndex={setTabIndex}
            setAddMore={setAddMore}
            workflowOverride={{
              isWorkflowInOverrideMode,
              setIsWorkflowInOverrideMode
            }}
            isRevisionMode={isRevisionMode}
            submittal={submittal}
            editableFields={editableFields}
            updateFeatureData={{
              localFeatureInstanceData,
              setLocalFeatureInstanceData
            }}
            submittalViewState={submittalViewState}
          />
        );
      case "2":
        return (
          <SubmittalAttachments
            editableFields={isRevisionMode ? [] : editableFields}
            documents={attachmentDocuments?.documents}
            referenceDocuments={attachmentDocuments?.referenceDocs}
            UploadAttachment={UploadAttachment}
            RemoveAttachment={RemoveAttachment}
            disabled={false}
            selectedDocument={selectedDocument}
            setSelectedDocument={(attachment: any) => {
              setSelectedDocument(attachment);
              if (paramAttachmentId) {
                history.replace(
                  `/project/${projectId}/submittals/${submittalId}?tab=2`
                );
              }
            }}
            findLatestAttachmentVersion={findLatestAttachmentVersion}
            isIntegrationMode={isIntegrationMode}
            addMore={addMore}
          />
        );
      default:
        return "";
    }
  };

  return (
    <div className="grow flex-col bg-ciq-gray-600">
      <FeatureHeader
        featureInstanceData={featureInstanceData}
        title="Submittal Details"
        subTitle=""
        actions={actions}
        number={localFeatureInstanceData?.auto_inc_value}
        headerTitle={localFeatureInstanceData?.title}
        disabled={
          !isRevisionMode &&
          !isIntegrationMode &&
          !cannotRespondToWorkflow &&
          editableFields.includes("title")
        }
        goBackToList={goBackToList}
        feature="SUBMITTALS"
        getTabIndex={setTabIndex}
        submittal={submittal}
        projectIntegrationConfig={projectIntegrationConfig}
        isIntegrationMode={isIntegrationMode}
        setRevisionMode={setRevisionMode}
        submittalHeaderMap={submittalHeaderMap}
        tokenContents={tokenContents}
        setSubmittalViewState={setSubmittalViewState}
        updateFeatureData={{
          localFeatureInstanceData,
          setLocalFeatureInstanceData
        }}
        gqlClientForProject={gqlClientForProject}
        isRevisionMode={isRevisionMode}
      />
      {submittalCardContainer()}
    </div>
  );
}

export default NewSubmittalDetailsPage;
